import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { personFileService } from '../services/api';
import {
  AlertTriangle, RefreshCw, ChevronRight, User, Building2, Clock, CheckCircle2,
  FileWarning, MailWarning, ClipboardList, Send, ShieldCheck, FileText, Loader2,
} from 'lucide-react';

const CATEGORY_META = {
  FAILED_DELIVERY: { label: 'Failed delivery / send issue', Icon: MailWarning, accent: 'border-red-300 bg-red-50', dot: 'bg-red-500', text: 'text-red-800' },
  NEEDS_ACTION_NOW: { label: 'Needs action now', Icon: AlertTriangle, accent: 'border-red-200 bg-red-50', dot: 'bg-red-500', text: 'text-red-800' },
  MISSING_INFORMATION: { label: 'Missing information', Icon: ClipboardList, accent: 'border-amber-200 bg-amber-50', dot: 'bg-amber-500', text: 'text-amber-800' },
  DOCUMENTS_RECEIVED_VERIFY: { label: 'Documents received — verify', Icon: ShieldCheck, accent: 'border-blue-200 bg-blue-50', dot: 'bg-blue-500', text: 'text-blue-800' },
  DOCUMENTS_EXPIRING_EXPIRED: { label: 'Documents expiring / expired', Icon: FileWarning, accent: 'border-red-200 bg-red-50', dot: 'bg-red-500', text: 'text-red-800' },
  FOLLOW_UP_DUE: { label: 'Follow-up due', Icon: Clock, accent: 'border-amber-200 bg-amber-50', dot: 'bg-amber-500', text: 'text-amber-800' },
  PACKET_READY: { label: 'Packet ready', Icon: Send, accent: 'border-green-200 bg-green-50', dot: 'bg-green-500', text: 'text-green-800' },
  READY_FOR_NEXT_STEP: { label: 'Ready for next step', Icon: ChevronRight, accent: 'border-green-200 bg-green-50', dot: 'bg-green-500', text: 'text-green-800' },
  PACKET_SENT_WAITING: { label: 'Packet sent — waiting', Icon: Clock, accent: 'border-slate-200 bg-slate-50', dot: 'bg-slate-400', text: 'text-slate-700' },
  COMPLETED: { label: 'Completed', Icon: CheckCircle2, accent: 'border-slate-200 bg-white', dot: 'bg-slate-300', text: 'text-slate-600' },
};

const FounderAttentionHome = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await personFileService.getAttention()); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed to load attention items'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [legalDocs, setLegalDocs] = useState([]);
  const [legalLoaded, setLegalLoaded] = useState(false);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [approving, setApproving] = useState(false);
  useEffect(() => { loadLegal(); }, []);

  const dl = async (docKey, fmt) => {
    try { const r = await personFileService.downloadLegalDocument(docKey, fmt); if (r.download_url) window.open(r.download_url, '_blank'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Download failed'); }
  };

  const loadLegal = async () => {
    try { const r = await personFileService.listLegalDocuments(); setLegalDocs(r.documents || []); }
    catch { /* non-blocking */ }
    finally { setLegalLoaded(true); }
  };
  const approve = async () => {
    if (!confirmDoc) return;
    setApproving(true);
    try { await personFileService.approveLegalDocument(confirmDoc.doc_key); setConfirmDoc(null); await loadLegal(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Approve failed (Founder / Super Admin only)'); }
    finally { setApproving(false); }
  };

  const order = data?.category_order || [];
  const grouped = {};
  (data?.items || []).forEach((it) => { (grouped[it.category] = grouped[it.category] || []).push(it); });
  const activeCategories = order.filter((c) => (grouped[c] || []).length > 0);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="attention-home">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">What needs my attention</h1>
            <p className="text-sm text-gray-500 mt-1">Everything actionable across people, companies, documents and packets — in one place.</p>
          </div>
          <button onClick={load} data-testid="attention-refresh" className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {err && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700" data-testid="attention-error">{err}</div>}

        <div className="mb-6 border border-gray-200 rounded-xl p-4 bg-white" data-testid="legal-docs-panel">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-indigo-800" />
            <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Legal Documents — Founder Review &amp; Approval</h2>
            <span className="ml-auto text-[11px] text-gray-400" data-testid="legal-docs-count">{legalDocs.length} document{legalDocs.length === 1 ? '' : 's'} available</span>
          </div>
          {legalDocs.length === 0 ? (
            <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center" data-testid="legal-docs-empty">
              {legalLoaded
                ? 'No legal documents are registered in this environment yet. If this is production, the completed documents still need to be registered here (they were prepared in preview).'
                : 'Loading legal documents…'}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {legalDocs.map((d) => (
                <div key={d.doc_key} className="flex flex-wrap items-start justify-between gap-2 min-w-0 border border-gray-100 rounded-lg px-3 py-2" data-testid={`legal-doc-${d.doc_key}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">{d.title}</p>
                    <p className="text-[11px] text-gray-400">{d.edition} · {d.from_version} → {d.version}</p>
                    <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${d.approval_status === 'founder_approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`legal-doc-status-${d.doc_key}`}>
                      {d.approval_status === 'founder_approved' ? 'FOUNDER APPROVED' : 'AWAITING FOUNDER APPROVAL'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => dl(d.doc_key, 'pdf')} data-testid={`legal-doc-pdf-${d.doc_key}`} className="text-xs font-medium text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded px-2 py-1">PDF</button>
                    <button onClick={() => dl(d.doc_key, 'docx')} data-testid={`legal-doc-docx-${d.doc_key}`} className="text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded px-2 py-1">DOCX</button>
                    {d.approval_status !== 'founder_approved' && (
                      <button onClick={() => setConfirmDoc(d)} data-testid={`legal-doc-approve-${d.doc_key}`} className="text-xs font-semibold text-white bg-green-700 hover:bg-green-800 rounded px-2 py-1">Approve</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!loading && data && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="attention-summary">
            {activeCategories.map((cat) => {
              const m = CATEGORY_META[cat];
              return (
                <span key={cat} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${m.accent} ${m.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {m.label} · {grouped[cat].length}
                </span>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-gray-400">Loading…</div>
        ) : (data?.total || 0) === 0 ? (
          <div className="py-20 text-center" data-testid="attention-empty">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">All clear — nothing needs your attention right now.</p>
            <p className="text-sm text-gray-400 mt-1">New items appear here as records move through onboarding and verification.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {activeCategories.map((cat) => {
              const m = CATEGORY_META[cat];
              const M = m.Icon;
              return (
                <section key={cat} data-testid={`attention-section-${cat}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <M className={`w-4 h-4 ${m.text}`} />
                    <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{m.label}</h2>
                    <span className="text-xs text-gray-400">({grouped[cat].length})</span>
                  </div>
                  <div className="space-y-2">
                    {grouped[cat].map((it) => (
                      <div key={`${it.kind}-${it.id}`} onClick={() => navigate(it.link)} data-testid={`attention-item-${it.id}`}
                        className={`flex items-center justify-between gap-3 border rounded-lg px-4 py-3 cursor-pointer hover:shadow-sm transition-shadow ${m.accent}`}>
                        <div className="min-w-0 flex items-start gap-3">
                          {it.kind === 'company' ? <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" /> : <User className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{it.name} <span className="text-xs font-normal text-gray-400">· {it.type}</span></p>
                            <p className="text-xs text-gray-600 break-words">{it.why}</p>
                            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400">
                              <span>{it.status_label}</span>
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {it.waiting_days != null && it.waiting_days > 0 ? `${it.waiting_days}d` : 'today'}</span>
                            </div>
                            <span className="sm:hidden inline-flex mt-1.5 text-xs font-medium text-blue-800 bg-white/70 border border-blue-100 rounded-full px-2.5 py-0.5" data-testid={`attention-action-${it.id}`}>{it.next_action}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="hidden sm:inline text-xs font-medium text-blue-800 bg-white/70 border border-blue-100 rounded-full px-3 py-1">{it.next_action}</span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {confirmDoc && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-4" data-testid="legal-approve-confirm" onClick={() => !approving && setConfirmDoc(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Approve legal document?</h3>
            <p className="text-sm text-gray-600 mt-2">
              Approve <span className="font-semibold text-gray-900">“{confirmDoc.title}”</span>{confirmDoc.version ? <> <span className="font-semibold text-gray-900">v{String(confirmDoc.version).replace(/^[vV]/, '')}</span></> : null} for BlueLock use?
            </p>
            <p className="text-[11px] text-gray-400 mt-1">This records your Founder approval for this exact document version. It does not send anything.</p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmDoc(null)} disabled={approving} data-testid="legal-approve-cancel" className="px-4 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={approve} disabled={approving} data-testid="legal-approve-confirm-btn" className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-lg disabled:opacity-50">
                {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Approve Document
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default FounderAttentionHome;
